import { createContext, useContext, useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Cookie, X } from "lucide-react";
import { Switch } from "../components/ui/switch";

const STORAGE_KEY = "nh-cookie-consent";
const CookieContext = createContext(null);
export const useCookieConsent = () => useContext(CookieContext);

export const CookieProvider = ({ children }) => {
  const [consent, setConsent] = useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  });
  const [showBanner, setShowBanner] = useState(consent === null);
  const [showSettings, setShowSettings] = useState(false);
  const [statsToggle, setStatsToggle] = useState(consent?.statistics ?? false);

  const save = (c) => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(c));
    setConsent(c);
    setShowBanner(false);
    setShowSettings(false);
  };

  const openSettings = () => {
    setStatsToggle(consent?.statistics ?? false);
    setShowSettings(true);
  };

  useEffect(() => {
    document.body.style.overflow = showSettings ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [showSettings]);

  return (
    <CookieContext.Provider value={{ consent, save, openSettings }}>
      {children}

      <AnimatePresence>
        {showBanner && !showSettings && (
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 30 }}
            transition={{ duration: 0.3, ease: "easeOut" }}
            className="fixed bottom-4 left-4 right-4 sm:right-auto sm:max-w-md z-[100] bg-[#171B21] text-[#F2F4F7] p-6 shadow-2xl border border-[#2A303A]"
            data-testid="cookie-banner"
          >
            <div className="flex items-start gap-3 mb-4">
              <Cookie className="w-5 h-5 mt-0.5 text-[#2E5BFF] shrink-0" />
              <p className="text-sm leading-relaxed text-[#9AA3AF]">
                Wir verwenden Cookies. Statistik-Cookies laden wir nur mit Ihrer Zustimmung.
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-2">
              <button
                data-testid="cookie-accept-all"
                onClick={() => save({ necessary: true, statistics: true })}
                className="btn-primary px-5 py-2.5 text-sm font-semibold flex-1"
              >
                Alle akzeptieren
              </button>
              <button
                data-testid="cookie-necessary-only"
                onClick={() => save({ necessary: true, statistics: false })}
                className="btn-ghost px-5 py-2.5 text-sm font-semibold flex-1"
              >
                Nur notwendige
              </button>
              <button
                data-testid="cookie-settings-open"
                onClick={openSettings}
                className="px-5 py-2.5 text-sm text-[#9AA3AF] hover:text-white transition-colors"
              >
                Einstellungen
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showSettings && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[110] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
            onClick={() => setShowSettings(false)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.97 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.97 }}
              transition={{ duration: 0.2, ease: "easeOut" }}
              onClick={(e) => e.stopPropagation()}
              className="bg-[#171B21] border border-[#2A303A] p-6 sm:p-8 max-w-lg w-full shadow-2xl"
              data-testid="cookie-settings-modal"
            >
              <div className="flex items-center justify-between mb-6">
                <h3 className="font-display text-2xl font-semibold">Cookie-Einstellungen</h3>
                <button data-testid="cookie-settings-close" onClick={() => setShowSettings(false)} className="p-2 hover:bg-white/10 transition-colors" aria-label="Schließen">
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="space-y-5">
                <div className="flex items-start justify-between gap-4 pb-5 border-b border-[#2A303A]">
                  <div>
                    <p className="font-semibold text-sm">Notwendige Cookies</p>
                    <p className="text-sm text-[#9AA3AF] mt-1">Erforderlich für Grundfunktionen wie das Speichern Ihrer Cookie-Auswahl. Immer aktiv.</p>
                  </div>
                  <Switch checked disabled data-testid="cookie-toggle-necessary" />
                </div>
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <p className="font-semibold text-sm">Statistik</p>
                    <p className="text-sm text-[#9AA3AF] mt-1">Anonyme Auswertung der Seitennutzung, um das Angebot zu verbessern. Wird erst nach Zustimmung geladen.</p>
                  </div>
                  <Switch checked={statsToggle} onCheckedChange={setStatsToggle} data-testid="cookie-toggle-statistics" />
                </div>
              </div>
              <button
                data-testid="cookie-save-settings"
                onClick={() => save({ necessary: true, statistics: statsToggle })}
                className="btn-primary px-6 py-3 text-sm font-semibold w-full mt-8"
              >
                Auswahl speichern
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </CookieContext.Provider>
  );
};
